import React from 'react';
import { Receipt } from 'lucide-react';
import { EmptyState } from '../../../components/ui/empty-state';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import ConversionesTablePage from '../components/ConversionesTablePage';
import { formatCLP, formatDateTime, formatNumber, formatUF } from '../../../styles/tokens/formatters';

const safeParseJson = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return {};

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const parseAmount = (amount) => {
  if (amount == null) return null;
  const raw = typeof amount === 'number' ? amount : String(amount).replace(',', '.');
  const n = typeof raw === 'number' ? raw : Number.parseFloat(raw);
  return Number.isNaN(n) ? null : n;
};

const formatAmountByCurrency = (amountNumber, currency) => {
  if (amountNumber == null) return 'N/A';
  if ((currency || '').toUpperCase() === 'UF') return formatUF(amountNumber);
  return `${formatNumber(amountNumber)} ${currency || ''}`.trim();
};

const parseNumber = (value) => {
  if (value == null || value === '') return null;
  const raw = typeof value === 'number' ? value : String(value).replace(',', '.');
  const n = typeof raw === 'number' ? raw : Number.parseFloat(raw);
  return Number.isNaN(n) ? null : n;
};

const getPaymentTrackingFromMetadata = (metadata) => {
  const chargedCLP =
    metadata?.charged_amount_clp ??
    metadata?.amount_clp ??
    metadata?.charged_clp ??
    metadata?.amount_pesos ??
    metadata?.clp_amount ??
    metadata?.payment?.amount_clp ??
    metadata?.payment?.charged_amount_clp ??
    metadata?.virtualpos_payment?.amount_clp ??
    null;

  const netCLP =
    metadata?.net_amount_clp ??
    metadata?.net_clp ??
    metadata?.recaudado_clp ??
    metadata?.payment?.net_amount_clp ??
    metadata?.virtualpos_payment?.net_amount_clp ??
    null;

  const feesCLP =
    metadata?.fees_clp ??
    metadata?.fee_clp ??
    metadata?.commissions_clp ??
    metadata?.commission_clp ??
    metadata?.payment?.fees_clp ??
    metadata?.payment?.commission_clp ??
    metadata?.virtualpos_payment?.fees_clp ??
    null;

  const depositDate =
    metadata?.deposit_date ??
    metadata?.deposit_at ??
    metadata?.deposited_at ??
    metadata?.settlement_date ??
    metadata?.settled_at ??
    metadata?.payment?.deposit_date ??
    metadata?.payment?.settlement_date ??
    metadata?.virtualpos_payment?.deposit_date ??
    null;

  const paidAt =
    metadata?.paid_at ??
    metadata?.transaction_date ??
    metadata?.processed_at ??
    metadata?.payment?.paid_at ??
    metadata?.virtualpos_payment?.paid_at ??
    null;

  const order = metadata?.charge?.payment?.order || null;
  const deposits = Array.isArray(order?.deposits) ? order.deposits : [];
  const firstDeposit = deposits?.[0] || null;

  const chargedFromOrder = parseNumber(order?.amount);
  const netFromDeposit = parseNumber(firstDeposit?.payout_amount);
  const feesFromDeposit = (() => {
    const processingFee = parseNumber(firstDeposit?.processing_fee);
    const flatFee = parseNumber(firstDeposit?.processing_flat_fee);
    if (processingFee === null && flatFee === null) return null;
    return (processingFee || 0) + (flatFee || 0);
  })();

  return {
    chargedCLP: parseNumber(chargedCLP) ?? chargedFromOrder,
    netCLP: parseNumber(netCLP) ?? netFromDeposit,
    feesCLP: parseNumber(feesCLP) ?? feesFromDeposit,
    depositDate: depositDate ?? firstDeposit?.payout_date ?? null,
    paidAt: paidAt ?? order?.authorized_at ?? order?.created_at ?? null
  };
};

const getChargeKeyFromCharge = (charge) => {
  if (charge?.external_charge_id) return charge.external_charge_id;
  if (charge?.id) return charge.id;
  const meta = safeParseJson(charge?.provider_metadata);
  return meta?.original_charge?.id || null;
};

const getChargeKeyFromProgram = (charge) => {
  if (charge?.id) return charge.id;
  if (charge?.internal_code) return charge.internal_code;
  if (charge?.external_charge_id) return charge.external_charge_id;
  return null;
};

const getSubscriptionFromMeta = (meta) => {
  const subscription = meta?.raw_response?.suscription || meta?.virtualpos_data?.raw_response?.suscription;
  return subscription && typeof subscription === 'object' ? subscription : {};
};

const getClientFromMeta = (meta) => {
  const subscription = getSubscriptionFromMeta(meta);
  const client = subscription?.client || {};

  const firstName = typeof client?.first_name === 'string' ? client.first_name.trim() : '';
  const lastName = typeof client?.last_name === 'string' ? client.last_name.trim() : '';
  const socialId = typeof client?.social_id === 'string' ? client.social_id.trim() : '';
  const email = typeof client?.email === 'string' ? client.email.trim() : '';
  const phone = typeof client?.phone === 'string' ? client.phone.trim() : '';

  return {
    name: [firstName, lastName].filter(Boolean).join(' '),
    socialId,
    email,
    phone
  };
};

const getTitularFromCharges = (charges) => {
  const list = Array.isArray(charges) ? charges : [];

  for (const ch of list) {
    const meta = safeParseJson(ch?.provider_metadata);
    const client = getClientFromMeta(meta);

    if (client?.name || client?.socialId) return client;
  }

  return { name: '', socialId: '', email: '', phone: '' };
};

const getTitularFromRow = (row) => {
  const charges = Array.isArray(row?.company_subscription_charges) ? row.company_subscription_charges : [];
  const fromCharges = getTitularFromCharges(charges);
  if (fromCharges?.name || fromCharges?.socialId) return fromCharges;

  const meta = safeParseJson(row?.provider_metadata);
  return getClientFromMeta(meta);
};

const getChargeProgram = (meta) => {
  const subscription = getSubscriptionFromMeta(meta);
  const program = subscription?.charge_program || meta?.raw_response?.charges_program || meta?.charges_program;
  return Array.isArray(program) ? program : [];
};

const normalizeStatus = (status) => (typeof status === 'string' ? status.toLowerCase() : '');

const getChargeTotals = (charges) => {
  const list = Array.isArray(charges) ? charges : [];

  let currency = '';
  let paid = 0;
  let pending = 0;
  let failed = 0;
  let paidCount = 0;
  let pendingCount = 0;
  let failedCount = 0;

  list.forEach((ch) => {
    const amount = parseAmount(ch?.amount);
    if (amount == null) return;
    if (!currency && typeof ch?.currency === 'string') currency = ch.currency;

    const status = normalizeStatus(ch?.status);
    if (['paid', 'pagado', 'approved'].includes(status)) {
      paid += amount;
      paidCount += 1;
      return;
    }
    if (['pending', 'pendiente', 'procesando', 'processing'].includes(status)) {
      pending += amount;
      pendingCount += 1;
      return;
    }
    if (['rejected', 'rechazado', 'failed', 'cancelado', 'canceled'].includes(status)) {
      failed += amount;
      failedCount += 1;
      return;
    }
    pending += amount;
    pendingCount += 1;
  });

  return { currency, paid, pending, failed, paidCount, pendingCount, failedCount };
};

const getNextChargeDate = (charges) => {
  const list = Array.isArray(charges) ? charges : [];
  const pendingDates = list
    .filter((ch) => !['paid', 'pagado', 'approved'].includes(normalizeStatus(ch?.status)))
    .map((ch) => ch?.charge_date)
    .filter(Boolean)
    .map((d) => new Date(d));

  const valid = pendingDates.filter((d) => !Number.isNaN(d.getTime()));
  if (valid.length === 0) return null;
  valid.sort((a, b) => a.getTime() - b.getTime());
  return valid[0].toISOString();
};

const getStatusClass = (status) => {
  const s = normalizeStatus(status);

  if (['paid', 'pagado', 'approved', 'activa', 'active'].includes(s)) return 'text-green-700';
  if (['pending', 'pendiente', 'procesando', 'suscribiendo', 'trialing', 'past_due'].includes(s)) return 'text-amber-600';
  if (['rejected', 'rechazado', 'failed', 'cancelada', 'canceled', 'finalizada', 'error', 'unpaid'].includes(s)) return 'text-[var(--yool-error)]';

  return 'text-muted-foreground';
};

const SubscriptionDetailDialog = ({ row }) => {
  const charges = Array.isArray(row?.company_subscription_charges) ? row.company_subscription_charges : [];
  const totals = getChargeTotals(charges);
  const meta = safeParseJson(row?.provider_metadata);
  const subscription = getSubscriptionFromMeta(meta);
  const chargeProgram = getChargeProgram(meta);
  const paymentMethod = subscription?.payment_method || {};
  const titular = getTitularFromRow(row);

  const nextChargeIso = getNextChargeDate(charges);
  const currency = row?.currency || subscription?.currency || totals.currency || '';
  const subscriptionAmount = parseAmount(row?.amount ?? subscription?.amount);

  const totalAmount = totals.paid + totals.pending + totals.failed;
  const pctPaid = totalAmount > 0 ? (totals.paid / totalAmount) * 100 : 0;
  const pctPending = totalAmount > 0 ? (totals.pending / totalAmount) * 100 : 0;
  const pctFailed = totalAmount > 0 ? (totals.failed / totalAmount) * 100 : 0;

  const paymentMethodData = [
    { label: 'Marca', value: paymentMethod?.brand },
    { label: 'Últimos 4', value: paymentMethod?.last4CardDigit },
    { label: 'Emisor', value: paymentMethod?.issuer },
    { label: 'Expira', value: paymentMethod?.expiration_date }
  ].filter((item) => item?.value !== null && item?.value !== undefined && String(item.value) !== '');

  const programMap = new Map();
  chargeProgram.forEach((program) => {
    const key = getChargeKeyFromProgram(program);
    if (!key) return;
    if (!programMap.has(key)) {
      programMap.set(key, program);
    }
  });

  const unifiedCharges = [];

  charges.forEach((charge) => {
    const key = getChargeKeyFromCharge(charge);
    const program = key ? programMap.get(key) : null;
    const metadata = safeParseJson(charge?.provider_metadata);
    const tracking = getPaymentTrackingFromMetadata(metadata);

    unifiedCharges.push({
      key: key || charge?.id || `${charge?.subscription_id}-${charge?.charge_date}-${charge?.external_charge_id || ''}`,
      source: program ? 'Ambos' : 'Suscripción',
      description: charge?.description || program?.description || 'Cuota',
      status: charge?.status || program?.status || 'N/A',
      chargeDate: charge?.charge_date || program?.charge_date,
      amount: parseAmount(charge?.amount ?? program?.amount),
      currency: charge?.currency || currency,
      chargedCLP: tracking.chargedCLP,
      netCLP: tracking.netCLP,
      feesCLP: tracking.feesCLP,
      paidAt: charge?.processed_at || tracking.paidAt,
      externalChargeId: charge?.external_charge_id || program?.id || key,
      virtualposChargeId: program?.id || key
    });

    if (key && programMap.has(key)) {
      programMap.delete(key);
    }
  });

  programMap.forEach((program, key) => {
    unifiedCharges.push({
      key: key || `${program?.charge_date}-${program?.description || ''}`,
      source: 'Programa',
      description: program?.description || 'Cuota',
      status: program?.status || 'Pendiente',
      chargeDate: program?.charge_date,
      amount: parseAmount(program?.amount),
      currency,
      chargedCLP: null,
      netCLP: null,
      feesCLP: null,
      paidAt: null,
      externalChargeId: key,
      virtualposChargeId: program?.id || key
    });
  });

  unifiedCharges.sort((a, b) => {
    const da = new Date(a?.chargeDate || 0);
    const db = new Date(b?.chargeDate || 0);
    return da.getTime() - db.getTime();
  });

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">Ver detalle</Button>
      </DialogTrigger>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto bg-white"
        style={{ width: '66vw', maxWidth: '66vw' }}
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle>Detalle de suscripción {row?.codigo || row?.id || ''}</DialogTitle>
              <div className="text-sm text-muted-foreground">VirtualPOS y programa de cobros</div>
            </div>
            <div className="flex items-center gap-2">
              {row?.status ? (
                <Badge appearance="outline" className={getStatusClass(row.status)}>{row.status}</Badge>
              ) : null}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Resumen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Plan</span>
                  <span className="font-medium text-right">{row?.plan_name || subscription?.plan_name || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Monto</span>
                  <span className="font-medium">{formatAmountByCurrency(subscriptionAmount, currency)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Recaudado</span>
                  <span className="font-medium">{formatAmountByCurrency(totals.paid, currency)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Por recaudar</span>
                  <span className="font-medium">{formatAmountByCurrency(totals.pending, currency)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Fallidos</span>
                  <span className="font-medium">{formatAmountByCurrency(totals.failed, currency)}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Próximos hitos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Próximo cobro</span>
                  <span className="font-medium">{nextChargeIso ? formatDateTime(nextChargeIso) : formatDateTime(row?.next_billing_date)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Inicio</span>
                  <span className="font-medium">{formatDateTime(row?.start_date)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Fin</span>
                  <span className="font-medium">{formatDateTime(row?.end_date)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Renovación</span>
                  <span className="font-medium">{row?.auto_renew == null ? 'N/A' : row.auto_renew ? 'Sí' : 'No'}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Titular</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Nombre</span>
                  <span className="font-medium text-right">{titular?.name || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">RUT</span>
                  <span className="font-medium">{titular?.socialId || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium text-right">{titular?.email || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Teléfono</span>
                  <span className="font-medium">{titular?.phone || 'N/A'}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Agotamiento de suscripción</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="h-10 overflow-hidden rounded-full bg-muted">
                <div className="flex h-full">
                  <div
                    className="h-full bg-[var(--chart-2)]"
                    style={{ width: `${pctPaid}%` }}
                  />
                  <div
                    className="h-full bg-[var(--chart-3)]"
                    style={{ width: `${pctPending}%` }}
                  />
                  <div
                    className="h-full bg-[var(--chart-4)]"
                    style={{ width: `${pctFailed}%` }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-[var(--chart-2)]" />
                    <span className="text-muted-foreground">Realizados</span>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{formatAmountByCurrency(totals.paid, currency)}</div>
                    <div className="text-xs text-muted-foreground">{formatNumber(totals.paidCount)} pagos</div>
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-[var(--chart-3)]" />
                    <span className="text-muted-foreground">Por realizar</span>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{formatAmountByCurrency(totals.pending, currency)}</div>
                    <div className="text-xs text-muted-foreground">{formatNumber(totals.pendingCount)} pagos</div>
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-[var(--chart-4)]" />
                    <span className="text-muted-foreground">Fallidos</span>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{formatAmountByCurrency(totals.failed, currency)}</div>
                    <div className="text-xs text-muted-foreground">{formatNumber(totals.failedCount)} pagos</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">VirtualPOS</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">ID suscripción</span>
                <div className="font-mono break-all">{row?.external_subscription_id || row?.subscription_id || subscription?.id || 'N/A'}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Plan ID</span>
                <div className="font-mono break-all">{row?.external_plan_id || subscription?.plan_id || 'N/A'}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Service ID</span>
                <div className="font-mono break-all">{subscription?.service_id || 'N/A'}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Proveedor</span>
                <div>{row?.payment_provider || 'N/A'}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Fecha suscripción</span>
                <div>{formatDateTime(subscription?.suscription_date || subscription?.subscription_date)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Canal</span>
                <div>{subscription?.channel || 'N/A'}</div>
              </div>
              {paymentMethodData.length > 0 ? (
                <div className="md:col-span-2 grid grid-cols-2 gap-3">
                  {paymentMethodData.map((item) => (
                    <div key={item.label}>
                      <span className="text-muted-foreground">{item.label}</span>
                      <div>{item.value}</div>
                    </div>
                  ))}
                </div>
              ) : null}
              {meta?.last_error?.message ? (
                <div className="md:col-span-2 text-[var(--yool-error)]">
                  {meta.last_error.message}
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Cobros</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="grid grid-cols-1 lg:grid-cols-[110px_minmax(220px,1fr)_120px_150px_130px_150px_150px_140px] gap-3">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Fuente</div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Cuota</div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Estado</div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Fecha</div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Monto</div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Cobrado (CLP)</div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Recaudado (CLP)</div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Comisión (CLP)</div>
              </div>

              <div className="space-y-2">
                {unifiedCharges.length === 0 ? (
                  <EmptyState
                    icon={<Receipt />}
                    title="Sin cobros"
                    description="No hay cobros registrados para esta suscripción."
                  />
                ) : (
                  unifiedCharges.map((row) => (
                    <div key={row.key} className="grid grid-cols-1 lg:grid-cols-[110px_minmax(220px,1fr)_120px_150px_130px_150px_150px_140px] gap-3 rounded-md border border-border/60 px-3 py-2">
                      <div>
                        <Badge appearance="outline">{row.source}</Badge>
                      </div>
                      <div className="min-w-0">
                        <div className="truncate">{row.description}</div>
                        <div className="text-xs text-muted-foreground truncate">ID: {row.externalChargeId || 'N/A'}</div>
                      </div>
                      <div className={getStatusClass(row.status)}>{row.status || 'N/A'}</div>
                      <div>{row.chargeDate ? formatDateTime(row.chargeDate) : 'N/A'}</div>
                      <div className="font-mono">{formatAmountByCurrency(row.amount, row.currency)}</div>
                      <div>
                        <div className="font-mono">{row.chargedCLP == null ? 'N/A' : formatCLP(row.chargedCLP)}</div>
                        <div className="text-xs text-muted-foreground">{row.paidAt ? formatDateTime(row.paidAt) : 'N/A'}</div>
                      </div>
                      <div className="font-mono">{row.netCLP == null ? 'N/A' : formatCLP(row.netCLP)}</div>
                      <div className="font-mono">{row.feesCLP == null ? 'N/A' : formatCLP(row.feesCLP)}</div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ConversionesSuscripciones = () => {
  return (
    <ConversionesTablePage
      title="Conversiones - Suscripciones"
      viewName="company_suscripciones"
      selectFields="id,created_at,compania_nombre,plan_name,status,amount,currency,billing_frequency,next_billing_date,codigo,subscription_id,external_subscription_id,external_plan_id,payment_provider,provider_metadata,start_date,end_date,auto_renew,company_subscription_charges(id,subscription_id,charge_date,amount,currency,status,description,provider_metadata,processed_at,external_charge_id)"
      defaultSortKey="created_at"
      defaultSortDirection="desc"
      columns={[
        { key: 'created_at', header: 'Fecha', render: (r) => formatDateTime(r?.created_at) },
        { key: 'compania_nombre', header: 'Compañía' },
        { key: 'plan_name', header: 'Plan' },
        { key: 'status', header: 'Estado' },
        {
          key: 'titular',
          header: 'Titular',
          sortable: false,
          render: (r) => {
            const titular = getTitularFromRow(r);
            if (titular?.name && titular?.socialId) return `${titular.name} • ${titular.socialId}`;
            return titular?.name || titular?.socialId || 'N/A';
          }
        },
        {
          key: 'recaudado',
          header: 'Recaudado',
          sortable: false,
          render: (r) => {
            const charges = Array.isArray(r?.company_subscription_charges) ? r.company_subscription_charges : [];
            const { currency, paid } = getChargeTotals(charges);
            return formatAmountByCurrency(paid, currency);
          }
        },
        {
          key: 'por_recaudar',
          header: 'Por recaudar',
          sortable: false,
          render: (r) => {
            const charges = Array.isArray(r?.company_subscription_charges) ? r.company_subscription_charges : [];
            const { currency, pending } = getChargeTotals(charges);
            return formatAmountByCurrency(pending, currency);
          }
        },
        {
          key: 'proximo_cobro',
          header: 'Próximo cobro',
          sortable: false,
          render: (r) => {
            const charges = Array.isArray(r?.company_subscription_charges) ? r.company_subscription_charges : [];
            const nextChargeIso = getNextChargeDate(charges);
            if (nextChargeIso) return formatDateTime(nextChargeIso);
            return formatDateTime(r?.next_billing_date);
          }
        },
        { key: 'codigo', header: 'Código' },
        {
          key: 'detalle',
          header: 'Detalle',
          sortable: false,
          render: (r) => <SubscriptionDetailDialog row={r} />
        }
      ]}
    />
  );
};

export default ConversionesSuscripciones;

